import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  styled,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  FiBox,
  FiShoppingCart,
  FiUsers,
  FiUser,
  FiTruck,
  FiTrendingDown,
  FiTrendingUp
} from "react-icons/fi";
import { getData } from "../Webservice/Webservice";
import MonthlySalesChart from "../components/MonthlySalesChart";
import TopProductsCharts from "../components/TopProductsCharts";
import OrdersStatusChart from "../components/OrdersStatusChart";
import OrdersPerDayChart from "../components/OrdersPerDayChart ";
import RecentOrders from "../components/RecentOrders";
import RecentCustomers from "../components/RecentCustomers";
import OutOfStockProducts from "../components/OutOfStockProducts";
import LowStockProducts from "../components/LowStockProducts";
import StockSummary from "../components/StockSummary";
import AdminDashboard from "./AdminDashboard";

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.3s ease"
}));

const DashboardHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(2)
  }
}));

// Helper to format change: +12% or -87.76%
const formatChange = change => {
  const sign = change >= 0 ? "+" : "";
  const formatted =
    Math.abs(change) % 1 === 0 ? Math.abs(change) : Math.abs(change).toFixed(2);
  return `${sign}${formatted}%`;
};

// Seller panel keeps the original dashboard; admins get the compact AdminDashboard.
const LegacyDashboard = () => {
  const theme = useTheme();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const data = await getData("dashboard/summary", null, token);

        if (data && data.success && data.summary) {
          setSummary(data);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        setError(err.message || "Failed to load dashboard summary");
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  // Build stats only when data is available
  const stats = summary
    ? [
        {
          id: 1,
          title: "Total Products",
          value: (summary.summary?.products?.count ?? 0).toLocaleString(),
          change: formatChange(summary.summary?.products?.change ?? 0),
          icon: <FiBox />,
          color: theme.palette.primary.main
        },
        {
          id: 2,
          title: "Total Orders",
          value: (summary.summary?.orders?.count ?? 0).toLocaleString(),
          change: formatChange(summary.summary?.orders?.change ?? 0),
          icon: <FiShoppingCart />,
          color: theme.palette.secondary.main
        },
        {
          id: 3,
          title: "Total Users",
          value: (summary.summary?.users?.count ?? 0).toLocaleString(),
          change: formatChange(summary.summary?.users?.change ?? 0),
          icon: <FiUsers />,
          color: theme.palette.success.main
        },
        {
          id: 4,
          title: "Total Customers",
          value: (summary.summary?.customers?.count ?? 0).toLocaleString(),
          change: formatChange(summary.summary?.customers?.change ?? 0),
          icon: <FiUser />,
          color: theme.palette.warning.main
        },
        {
          id: 5,
          title: "Total Sellers",
          value: (summary.summary?.vendors?.count ?? summary.summary?.sellers?.count ?? 0).toLocaleString(),
          change: formatChange(summary.summary?.vendors?.change ?? summary.summary?.sellers?.change ?? 0),
          icon: <FiTruck />,
          color: theme.palette.info.main
        }
      ]
    : [];

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <DashboardHeader>
        <Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: "text.primary",
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontFamily: "Mulish, sans-serif"
            }}
          >
            Dashboard
          </Typography>
        </Box>
      </DashboardHeader>

      {/* Loading */}
      {loading &&
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>}

      {/* Error */}
      {error &&
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>}

      {/* Stats Cards */}
      {!loading &&
        !error &&
        stats.length > 0 &&
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map(stat =>
            <Grid item xs={12} sm={6} md={2.4} key={stat.id}>
              <StatCard>
                <CardContent>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        gutterBottom
                      >
                        {stat.title}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: `${stat.color}20`,
                        color: stat.color
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: stat.change.startsWith("+")
                          ? "success.main"
                          : "error.main",
                        mr: 1
                      }}
                    >
                      {stat.change.startsWith("+")
                        ? <FiTrendingUp size={16} />
                        : <FiTrendingDown size={16} />}
                      <Typography
                        variant="body2"
                        sx={{ ml: 0.5, fontWeight: 600 }}
                      >
                        {stat.change}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      vs last month
                    </Typography>
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
          )}
        </Grid>}

      <Box>
        <Grid container spacing={3} alignItems="stretch">
          {/* Left Chart - 70% */}
          <Grid item xs={12} md={7.8}>
            <Box sx={{ height: "100%" }}>
              <MonthlySalesChart />
            </Box>
          </Grid>

          {/* Right Chart - 30% */}
          <Grid item xs={12} md={4.2}>
            <Box sx={{ height: "100%" }}>
              <OrdersStatusChart />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <TopProductsCharts />
      <OrdersPerDayChart />
      <StockSummary />

      <Box>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Box sx={{ height: "100%" }}>
              <OutOfStockProducts />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ height: "100%" }}>
              <LowStockProducts />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* <OutOfStockProducts /> */}
      {/* <LowStockProducts /> */}
      <RecentOrders />
      <RecentCustomers />
    </Box>
  );
};

const Dashboard = () => {
  const isVendor = (sessionStorage.getItem("userType") || "").toLowerCase() === "vendor";
  return isVendor ? <LegacyDashboard /> : <AdminDashboard />;
};

export default Dashboard;
